import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { User, Lock, LogIn, Loader2, Eye, EyeOff, Receipt, X, Shield, FileText, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Subdomain to Tenant mapping for custom portal domains
const PORTAL_DOMAIN_MAPPING = {
  'portal.radiuslink.id': 'T06122503',
};

// Get tenant code from subdomain or URL params
const getTenantCode = (urlTenantSlug) => {
  const hostname = window.location.hostname;
  return PORTAL_DOMAIN_MAPPING[hostname] || urlTenantSlug;
};

const TenantCustomerLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paymentInvoiceParam = searchParams.get("invoice");
  const { tenantSlug: urlTenantSlug } = useParams();
  const tenantSlug = getTenantCode(urlTenantSlug);
  const [accountNumber, setAccountNumber] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // 2026-02: Login error popup state
  const [errorPopup, setErrorPopup] = useState({ open: false, title: '', message: '' });
  const [legalModal, setLegalModal] = useState(null); // null | 'privacy' | 'terms'
  const [tenantInfo, setTenantInfo] = useState(null);
  const [loadingTenant, setLoadingTenant] = useState(true);
  const [tenantError, setTenantError] = useState(null);

  useEffect(() => {
    if (tenantSlug) {
      fetchTenantInfo();
    }
  }, [tenantSlug]);

  const fetchTenantInfo = async () => {
    try {
      setLoadingTenant(true);
      const response = await axios.get(`${API_URL}/api/portal/${tenantSlug}/info`);
      if (response.data.success) {
        setTenantInfo(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching tenant info:', error);
      setTenantError('Portal tidak ditemukan');
    } finally {
      setLoadingTenant(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!accountNumber || !password) {
      setErrorPopup({ open: true, title: 'Data Belum Lengkap', message: 'Mohon isi No Akun / No HP dan Password terlebih dahulu.' });
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/v2/portal/${tenantSlug}/login`, null, {
        params: {
          account_number: accountNumber,
          password: password
        }
      });

      if (response.data.success) {
        const token = response.data.data.access_token;
        const customerData = response.data.data.customer;
        
        localStorage.setItem('customer_token', token);
        localStorage.setItem('customer_data', JSON.stringify(customerData));
        localStorage.setItem('tenant_slug', tenantSlug);
        
        toast.success(`Selamat datang, ${customerData.name}!`);
        
        await new Promise(resolve => setTimeout(resolve, 50));
        
        navigate(paymentInvoiceParam
          ? `/p/${tenantSlug}/dashboard?invoice=${encodeURIComponent(paymentInvoiceParam)}`
          : `/p/${tenantSlug}/dashboard`);
      }
    } catch (error) {
      console.error('Login error:', error);
      const detail = String(error.response?.data?.detail || '');
      const status = error.response?.status;
      let popupTitle = 'Login Gagal';
      let popupMessage = 'Terjadi kesalahan. Silakan coba lagi.';
      if (status === 404 || /tidak ditemukan|not.*found|customer not|akun.*tidak/i.test(detail)) {
        popupTitle = 'Akun Tidak Ditemukan';
        popupMessage = 'No Akun atau No HP yang Anda masukkan tidak terdaftar. Periksa kembali atau hubungi Customer Service.';
      } else if (status === 401 || /password.*salah|salah.*password|incorrect|invalid.*credential/i.test(detail)) {
        popupTitle = 'Password Salah';
        popupMessage = 'Password yang Anda masukkan tidak sesuai. Coba lagi atau gunakan tombol Lupa Password di bawah.';
      } else if (status === 403 || /nonaktif|inactive|disabled|diblokir/i.test(detail)) {
        popupTitle = 'Akun Tidak Aktif';
        popupMessage = 'Akun Anda saat ini tidak aktif. Silakan hubungi Customer Service untuk aktivasi.';
      } else if (detail) {
        popupMessage = detail;
      }
      setErrorPopup({ open: true, title: popupTitle, message: popupMessage });
    } finally {
      setLoading(false);
    }
  };

  // TENANT_DYNAMIC_THEME_V1
  // Map tenant_code -> Tailwind palette. Default: orange (KUMARA legacy). NJnet: green.
  const THEME_MAP = {
    'T04102601': {
      // MIFA NET — navy biru + aksen hijau (selaras logo: teks navy, daun hijau, swoosh)
      bgGrad: 'from-sky-50 via-white to-blue-100',
      ribbon: 'from-blue-900 via-sky-600 to-emerald-500',
      pillBg: 'from-sky-50 to-emerald-50',
      pillBorder: 'border-sky-100',
      pillDot: 'from-sky-500 to-emerald-500',
      pillText: 'from-blue-800 to-sky-600',
      cardBorder: 'border-sky-100/60',
      cardShadow: 'shadow-[0_20px_60px_-15px_rgba(22,48,90,0.25)]',
      ring: 'focus:ring-sky-500 focus:border-sky-400',
      spinner: 'text-sky-600 dark:text-sky-400',
      btnBg: 'bg-gradient-to-r from-blue-900 via-sky-700 to-emerald-500 hover:from-blue-950 hover:via-sky-800 hover:to-emerald-600',
      btnShadow: 'shadow-sky-500/30 hover:shadow-sky-500/40',
      linkText: 'text-sky-700 hover:text-sky-800',
      brandColor: '#16305A',
      brandColorDark: '#0E2240',
      brandGradient: 'linear-gradient(135deg, #1F4E79 0%, #16305A 45%, #0E2240 100%)',
      ribbonGradient: 'linear-gradient(90deg, #16305A 0%, #1F6FB2 55%, #4CAF50 100%)',
    },
    'T14022601': {
      // NJnet — green
      bgGrad: 'from-emerald-50 via-white to-emerald-100',
      ribbon: 'from-emerald-500 via-teal-500 to-green-600',
      pillBg: 'from-emerald-50 to-teal-50',
      pillBorder: 'border-emerald-100',
      pillDot: 'from-emerald-500 to-teal-600',
      pillText: 'from-emerald-600 to-teal-600',
      cardBorder: 'border-emerald-100/60',
      cardShadow: 'shadow-[0_20px_60px_-15px_rgba(16,185,129,0.25)]',
      ring: 'focus:ring-emerald-500 focus:border-emerald-400',
      spinner: 'text-emerald-600 dark:text-emerald-400',
      btnBg: 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700',
      btnShadow: 'shadow-emerald-500/30 hover:shadow-emerald-500/40',
      linkText: 'text-emerald-600 hover:text-emerald-700',
      brandColor: '#059669',
      brandColorDark: '#047857',
      brandGradient: 'linear-gradient(135deg, #10B981 0%, #059669 45%, #047857 100%)',
      ribbonGradient: 'linear-gradient(90deg, #10B981 0%, #059669 50%, #047857 100%)',
    },
  };
  const _tCode = tenantInfo?.tenant_code || tenantInfo?.tenantCode || '';
  const T = THEME_MAP[_tCode] || {
    bgGrad: 'from-red-50 via-white to-red-100',
    ribbon: 'from-orange-500 via-red-500 to-red-600',
    pillBg: 'from-orange-50 to-red-50',
    pillBorder: 'border-orange-100',
    pillDot: 'from-orange-500 to-red-600',
    pillText: 'from-orange-600 to-red-600',
    cardBorder: 'border-orange-100/60',
    cardShadow: 'shadow-[0_20px_60px_-15px_rgba(249,115,22,0.25)]',
    ring: '${T.ring}',
    spinner: '${T.spinner}',
    btnBg: 'bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700',
    btnShadow: 'shadow-orange-500/30 hover:shadow-orange-500/40',
    linkText: 'text-orange-600 hover:text-orange-700',
    brandColor: '#EA580C',
    brandColorDark: '#C2410C',
    brandGradient: 'linear-gradient(135deg, #F97316 0%, #EA580C 45%, #DC2626 100%)',
    ribbonGradient: 'linear-gradient(90deg, #F97316 0%, #EA580C 50%, #DC2626 100%)',
  };
  // TENANT_DYNAMIC_THEME_V2

  if (loadingTenant) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F5F7FA' }}>
        <Loader2 className={`w-8 h-8 animate-spin ${T.spinner}`} />
      </div>
    );
  }

  if (tenantError) {
    return (
      <div className={`min-h-screen bg-gradient-to-br ${T.bgGrad} flex items-center justify-center p-4`}>
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center dark:bg-slate-800">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 dark:bg-red-900/40">
            <span className="text-3xl">❌</span>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2 dark:text-slate-100">Portal Tidak Ditemukan</h2>
          <p className="text-gray-600 mb-6 dark:text-slate-300">Portal dengan kode <strong>"{tenantSlug}"</strong> tidak ditemukan atau tidak aktif.</p>
          <button
            onClick={() => navigate('/login')}
            className="text-blue-600 hover:text-blue-800 font-medium dark:text-blue-400"
          >
            Kembali ke Login Admin
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative min-h-screen flex items-center justify-center p-4 sm:p-6 overflow-hidden scrollbar-hide"
      style={{ background: '#F5F7FA' }}
    >
      {/* Kumara brand top ribbon — thin gradient bar for identity without noisy background */}
      <div className="pointer-events-none absolute top-0 left-0 right-0 h-1.5"
        style={{ background: T.ribbonGradient }}
        aria-hidden
      />

      <div className="max-w-md w-full relative z-10">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          {(tenantInfo?.branding?.logo_portal_url || tenantInfo?.branding?.logo_url) ? (
            <div className="flex justify-center mb-3">
              <img 
                src={tenantInfo.branding.logo_portal_url || tenantInfo.branding.logo_url} 
                alt={tenantInfo.tenant_name}
                className="h-20 sm:h-24 w-auto object-contain"
                style={{ mixBlendMode: 'multiply' }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          ) : (
            <div className="flex justify-center mb-3">
              <div 
                className="w-16 h-16 rounded-xl flex items-center justify-center text-white text-2xl font-bold"
                style={{ background: `linear-gradient(135deg, ${tenantInfo?.branding?.primary_color || '#3b82f6'} 0%, ${tenantInfo?.branding?.secondary_color || '#1d4ed8'} 100%)`, color: '#fff' }}
              >
                {tenantInfo?.tenant_name?.charAt(0) || 'P'}
              </div>
            </div>
          )}
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight dark:text-slate-100" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>{tenantInfo?.tenant_name || 'Portal Pelanggan'}</h1>  {/* // TENANT_LOGIN_FONT_LOGO_V1 */}
          <div className={`inline-flex items-center gap-1.5 mt-2 px-3 py-1 rounded-full bg-gradient-to-r ${T.pillBg} border ${T.pillBorder}`}>
            <span className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${T.pillDot} animate-pulse`} />
            <p className={`text-[11px] font-semibold uppercase tracking-wider bg-gradient-to-r ${T.pillText} bg-clip-text text-transparent`}>Portal Pelanggan</p>
          </div>
        </div>

        {/* Login Card */}
        <div className={`bg-white rounded-3xl ${T.cardShadow} p-6 sm:p-8 border ${T.cardBorder} backdrop-blur-sm dark:bg-slate-800`}>
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-1 text-center tracking-tight dark:text-slate-100">
            Masuk ke Akun Anda
          </h2>
          <p className="text-xs text-gray-500 text-center mb-5 sm:mb-6 dark:text-slate-400">Masukkan no akun atau no hp anda</p>

          <form onSubmit={handleLogin} className="space-y-5">
            {/* Account Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                No Akun / No HP
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  placeholder="Contoh: 25112000001, 6281112393993, atau 082112393993"
                  className={`w-full pl-11 pr-4 py-3 sm:py-3.5 border border-gray-200 rounded-xl bg-gray-50/60 focus:bg-white focus:ring-2 ${T.ring} transition-colors text-sm sm:text-base dark:border-slate-700`}
                  disabled={loading}
                />
              </div>
              <p className="text-[11px] text-gray-500 mt-1.5 dark:text-slate-400">No HP boleh diawali <b>62</b> atau <b>0</b></p>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Masukkan password Anda"
                  className={`w-full pl-11 pr-11 py-3 sm:py-3.5 border border-gray-200 rounded-xl bg-gray-50/60 focus:bg-white focus:ring-2 ${T.ring} transition-colors text-sm sm:text-base dark:border-slate-700`}
                  disabled={loading}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label="Toggle password visibility"
                  data-testid="toggle-password"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <div className="flex justify-end mt-2">
                <a
                  href={tenantInfo?.contact_phone ? `https://wa.me/${(tenantInfo.contact_phone||'').replace(/[^0-9]/g,'')}?text=Halo,%20saya%20lupa%20password%20portal%20pelanggan` : '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-semibold hover:underline"
                  style={{ color: T.brandColor }}
                  data-testid="forgot-password-link"
                >
                  Lupa Password?
                </a>
              </div>
            </div>

            {/* Login Button */}
            <button
              type="submit"
              disabled={loading}
              className={`w-full text-white font-bold py-3.5 sm:py-4 rounded-xl flex items-center justify-center gap-2 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg ${T.btnShadow} hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 tracking-wide`}
              style={{
                background: T.brandGradient,
              }}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Memproses...</span>
                </>
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  <span>Masuk</span>
                </>
              )}
            </button>
          </form>{/* Bottom Help Notice */}
          <div className="mt-6 pt-6 border-t border-gray-200 text-center dark:border-slate-700">
            <p className="text-xs text-gray-500 dark:text-slate-400">
              Lupa ID Pelanggan? Hubungi Customer Service
            </p>
            {tenantInfo?.contact_phone && (
              <a
                href={`https://wa.me/${(tenantInfo.contact_phone||'').replace(/[^0-9]/g,'')}`}
                target="_blank" rel="noopener noreferrer"
                className="inline-block mt-1 text-sm font-bold hover:underline"
                style={{ color: T.brandColor }}
                data-testid="cs-phone-link"
              >
                {tenantInfo.contact_phone}
              </a>
            )}
          </div>

          {/* Legal Links */}
          <div className="mt-5 text-center" data-testid="legal-links">
            <p className="text-[11px] text-gray-400 leading-relaxed">
              Situs ini dilindungi oleh kebijakan kami.
            </p>
            <div className="mt-1 flex items-center justify-center gap-2 text-xs font-semibold">
              <button
                type="button"
                onClick={() => setLegalModal('privacy')}
                className="hover:underline"
                style={{ color: T.brandColor }}
                data-testid="open-privacy"
              >
                Kebijakan Privasi
              </button>
              <span className="text-gray-300">•</span>
              <button
                type="button"
                onClick={() => setLegalModal('terms')}
                className="hover:underline"
                style={{ color: T.brandColor }}
                data-testid="open-terms"
              >
                Persyaratan Layanan
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-gray-500 dark:text-slate-400">
          <p>© {new Date().getFullYear()} {tenantInfo?.tenant_name || 'RadiusLink'}. Hak Cipta Dilindungi.</p>
        </div>

        {/* Legal Modals — Privacy + Terms */}
        {legalModal && (
          <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50 backdrop-blur-sm"
            onClick={() => setLegalModal(null)}
            data-testid="legal-modal"
          >
            <div
              className="bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh] sm:max-h-[85vh] animate-in slide-in-from-bottom-4 duration-200 dark:bg-slate-800"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200 sticky top-0 bg-white dark:bg-slate-800">
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: `${tenantInfo?.branding?.primary_color || '#f97316'}1a`, color: tenantInfo?.branding?.primary_color || '#f97316' }}
                  >
                    {legalModal === 'privacy' ? <Shield className="w-5 h-5" /> : <FileText className="w-5 h-5" />}
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base sm:text-lg font-bold text-neutral-800 leading-tight truncate">
                      {legalModal === 'privacy' ? 'Kebijakan Privasi' : 'Persyaratan Layanan'}
                    </h3>
                    <p className="text-[11px] text-neutral-500 truncate">{tenantInfo?.tenant_name || 'Portal Pelanggan'}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setLegalModal(null)}
                  className="w-9 h-9 rounded-full hover:bg-neutral-100 flex items-center justify-center text-neutral-500"
                  aria-label="Tutup"
                  data-testid="close-legal-modal"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Body scrollable */}
              <div className="px-5 py-4 sm:py-5 overflow-y-auto text-sm text-neutral-700 leading-relaxed space-y-4 hide-scrollbar">
                {legalModal === 'privacy' ? (
                  <>
                    <p className="text-[11px] text-neutral-400">Terakhir diperbarui: {new Date().toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
                    <p>
                      {tenantInfo?.tenant_name || 'Kami'} ("Kami") menghargai privasi Anda. Kebijakan ini menjelaskan bagaimana kami mengumpulkan, menggunakan, dan melindungi data pribadi pelanggan portal layanan internet kami.
                    </p>
                    <h4 className="font-bold text-neutral-800">1. Data yang Kami Kumpulkan</h4>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>Identitas pelanggan: nama, ID pelanggan, alamat instalasi</li>
                      <li>Kontak: nomor telepon, alamat email</li>
                      <li>Data layanan: paket berlangganan, riwayat tagihan, status koneksi</li>
                      <li>Data teknis: alamat IP, MAC, log penggunaan jaringan untuk troubleshooting</li>
                    </ul>
                    <h4 className="font-bold text-neutral-800">2. Penggunaan Data</h4>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>Memberikan layanan internet dan dukungan teknis</li>
                      <li>Pengiriman tagihan, notifikasi pembayaran, dan pengingat jatuh tempo</li>
                      <li>Komunikasi penting terkait gangguan layanan atau maintenance</li>
                      <li>Memenuhi kewajiban hukum dan regulasi telekomunikasi Indonesia</li>
                    </ul>
                    <h4 className="font-bold text-neutral-800">3. Keamanan Data</h4>
                    <p>Data pelanggan disimpan di server kami dengan enkripsi standar industri. Akses ke data terbatas pada staf yang berwenang. Kami tidak menjual atau menyewakan data pribadi pelanggan kepada pihak ketiga.</p>
                    <h4 className="font-bold text-neutral-800">4. Hak Pelanggan</h4>
                    <p>Anda berhak meminta akses, koreksi, atau penghapusan data pribadi Anda dengan menghubungi Customer Service kami di {tenantInfo?.contact_phone || 'nomor terdaftar'}.</p>
                    <h4 className="font-bold text-neutral-800">5. Perubahan Kebijakan</h4>
                    <p>Kebijakan ini dapat diperbarui sewaktu-waktu. Versi terbaru akan tersedia di halaman ini.</p>
                  </>
                ) : (
                  <>
                    <p className="text-[11px] text-neutral-400">Terakhir diperbarui: {new Date().toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
                    <p>
                      Dengan menggunakan layanan {tenantInfo?.tenant_name || 'Kami'}, Anda menyetujui persyaratan berikut:
                    </p>
                    <h4 className="font-bold text-neutral-800">1. Layanan</h4>
                    <p>Kami menyediakan layanan internet broadband sesuai dengan paket yang Anda pilih. Kecepatan tertera bersifat "up to" dan dapat dipengaruhi oleh kondisi jaringan, perangkat pelanggan, dan trafik global.</p>
                    <h4 className="font-bold text-neutral-800">2. Pembayaran</h4>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>Tagihan diterbitkan setiap bulan sesuai siklus billing Anda</li>
                      <li>Pembayaran wajib dilunasi sebelum tanggal jatuh tempo</li>
                      <li>Keterlambatan dapat mengakibatkan isolasi/pembatasan layanan</li>
                      <li>Reaktivasi setelah isolasi mungkin dikenakan biaya administrasi</li>
                    </ul>
                    <h4 className="font-bold text-neutral-800">3. Penggunaan yang Dilarang</h4>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>Pelanggaran hukum atau peraturan yang berlaku di Indonesia</li>
                      <li>Penggunaan layanan untuk aktivitas ilegal, spam, atau peretasan</li>
                      <li>Membagi/menjual koneksi tanpa izin tertulis dari kami</li>
                      <li>Mengganggu jaringan atau pengguna lain</li>
                    </ul>
                    <h4 className="font-bold text-neutral-800">4. Perangkat & Instalasi</h4>
                    <p>Perangkat ONT/Router yang dipinjamkan tetap menjadi milik kami. Kerusakan akibat kelalaian pelanggan dapat dikenakan biaya penggantian.</p>
                    <h4 className="font-bold text-neutral-800">5. Pemutusan Layanan</h4>
                    <p>Pelanggan dapat berhenti berlangganan dengan pemberitahuan tertulis minimal 7 hari sebelumnya. Tagihan sampai tanggal pemutusan tetap menjadi tanggung jawab pelanggan.</p>
                    <h4 className="font-bold text-neutral-800">6. Force Majeure</h4>
                    <p>Kami tidak bertanggung jawab atas gangguan layanan akibat bencana alam, kerusakan infrastruktur publik, atau hal lain di luar kendali kami.</p>
                    <h4 className="font-bold text-neutral-800">7. Kontak</h4>
                    <p>Pertanyaan terkait persyaratan ini dapat disampaikan ke Customer Service di {tenantInfo?.contact_phone || 'nomor terdaftar'}.</p>
                  </>
                )}
              </div>

              {/* Footer */}
              <div className="px-5 py-3 border-t border-neutral-200 bg-neutral-50">
                <button
                  type="button"
                  onClick={() => setLegalModal(null)}
                  className="w-full py-2.5 rounded-lg text-sm font-semibold text-white"
                  style={{ background: tenantInfo?.branding?.primary_color || '#f97316' }}
                  data-testid="agree-legal"
                >
                  Mengerti
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 2026-02: Login Error Popup */}
        {errorPopup.open && (
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setErrorPopup({ ...errorPopup, open: false })}
            data-testid="login-error-popup"
          >
            <div
              className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-sm w-full mx-4 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className={`h-2 bg-gradient-to-r ${T.ribbon}`}></div>
              <div className="p-6 text-center">
                <div className="mx-auto w-16 h-16 rounded-full bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center mb-4">
                  <AlertTriangle className="w-8 h-8 text-rose-600 dark:text-rose-400" />
                </div>
                <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-2" data-testid="login-error-title">{errorPopup.title}</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mb-5" data-testid="login-error-message">{errorPopup.message}</p>
                <button
                  type="button"
                  onClick={() => setErrorPopup({ ...errorPopup, open: false })}
                  className={`w-full py-3 rounded-xl text-white font-semibold text-sm ${T.btnBg} shadow-lg ${T.btnShadow} transition-all active:scale-95`}
                  data-testid="login-error-close-btn"
                >
                  OK, Mengerti
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TenantCustomerLogin;
